import { Check, CircleCheck, Undo2, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../components/index-page/types';
import type { MenuItem } from '../../../components/Menu';
import { DateCell } from '../../../components/table/DateCell';
import { StatusText } from '../../../components/table/StatusText';
import { TextCell } from '../../../components/table/TextCell';
import { usePermission } from '../../../auth/AuthContext';
import { useToast } from '../../../components/toastContext';
import { ApiError } from '../../../lib/apiError';
import { RejectApprovalModal } from '../components/RejectApprovalModal';
import { approvalsApi } from '../approvalsApi';
import { APPROVAL_STATUS, DECISION_FAILED, decisionMessage } from '../approvalTypes';
import type { ApprovalRequestListItem } from '../approvalTypes';

/**
 * The approval inbox — what is waiting on this account, and what it has decided.
 *
 * **There is no Create, and the absence is the engine's shape rather than unfinished work.** A
 * request is submitted by the owning module's own form, behind that module's key; nothing is
 * created here. Nor is there Clear data: a concluded request is the record of a decision
 * somebody made.
 */
export function ApprovalsIndexPage() {
  const navigate = useNavigate();
  const canReadAll = usePermission('approvals.read-all');
  const toast = useToast();

  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [rejecting, setRejecting] = useState<ApprovalRequestListItem | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRequests = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      approvalsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          workflow: query.facets.workflow?.join(','),
          scope,
        },
        signal,
      ),
    [scope],
  );

  /**
   * Records a decision taken from a row, and says what became of it.
   *
   * **BOTH OUTCOMES GO TO THE TOAST.** A success refreshes the list, and a row that quietly
   * moves out of `Pending` is a reader inferring the result from what is no longer there — on
   * the one surface where they never left the list to make the decision.
   *
   * A failure used to render a hand-rolled strip pinned to the bottom of this page at a raw
   * `z-50`, which is the number `layers.ts` exists to stop a call site choosing. It was also
   * failure-only and never dismissible. The toast is the same place on screen, from the shared
   * component, at the `TOAST` rung.
   */
  const decide = useCallback(
    async (verb: string, subject: string | null, action: () => Promise<unknown>) => {
      try {
        await action();
        refresh();
        toast.success(decisionMessage(verb, subject));
      } catch (caught) {
        toast.failure(caught instanceof ApiError ? caught.message : DECISION_FAILED);
      }
    },
    [refresh, toast],
  );

  /*
    EVERY ENTRY CARRIES THE KEY OF WHAT IT DOES, and each is DISABLED WITH A REASON rather than
    hidden when it does not apply: the reader holds the permission, so a vanished entry would
    say they lack it. Whether they may decide THIS request is the step's business, which is a
    different question from the key — and `awaitingMe` is what the row already knows.
  */
  const rowActions = useCallback(
    (request: ApprovalRequestListItem): MenuItem[] => [
      {
        key: `approve-${request.id}`,
        label: 'Approve',
        icon: <Check className="h-4 w-4" aria-hidden="true" />,
        permission: 'approvals.decide',
        disabledReason: request.awaitingMe ? null : 'Nothing on this request is awaiting you.',
        onClick: () => void decide('Approved', request.subjectReference, () => approvalsApi.approve(request.id)),
      },
      {
        key: `acknowledge-${request.id}`,
        label: 'Acknowledge',
        icon: <CircleCheck className="h-4 w-4" aria-hidden="true" />,
        permission: 'approvals.decide',
        disabledReason: request.awaitingMe ? null : 'Nothing on this request is awaiting you.',
        onClick: () => void decide('Acknowledged', request.subjectReference, () => approvalsApi.acknowledge(request.id)),
      },
      {
        key: `retract-${request.id}`,
        label: 'Retract',
        icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
        // The submitter's own withdrawal, so the READ key — not the approver's.
        permission: 'approvals.read',
        disabledReason:
          request.status === APPROVAL_STATUS.pending
            ? null
            : 'Only a pending request can be retracted.',
        onClick: () => void decide('Retracted', request.subjectReference, () => approvalsApi.retract(request.id)),
      },
      {
        key: `reject-${request.id}`,
        label: 'Reject',
        icon: <X className="h-4 w-4" aria-hidden="true" />,
        destructive: true,
        permission: 'approvals.decide',
        disabledReason: request.awaitingMe ? null : 'Nothing on this request is awaiting you.',
        onClick: () => setRejecting(request),
      },
    ],
    [decide],
  );

  return (
    <>
      <IndexPage<ApprovalRequestListItem>
        module="approvals"
        breadcrumb={['Approvals', 'Requests']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Decision' },
          { key: 'workflow', label: 'Workflow' },
        ]}
        /*
          FILED BY DECISION, not by the row's live-or-deleted state. A reader opens this page to
          separate what is still waiting from what has been decided, which is `stage`; `status`
          stays in the rail with Show deleted, where it is on every list in the product.
        */
        folderNavFacet="stage"
        /*
          IT LANDS ON PENDING. Product call, 01 Oct 2026 — an inbox is opened to answer what is
          WAITING, and this one led with every concluded request, so the reader's first action
          was a click they took every single time.

          A SEED, not a filter: the `All` tab is still there and still clears it, so nothing is
          hidden — and a URL naming a facet wins, so a refresh lands back where they were.
        */
        initialFacets={{ stage: ['Pending'] }}
        /*
          THE SCOPE IS A COG ENTRY, not a facet. It changes what the list IS rather than
          narrowing what is in it, and no row can be filtered by it — whether a request is
          "mine" is a property of who is asking. Not persisted, for the reason Show deleted is
          not: a view that quietly kept showing everybody's queue is how a reader mistakes what
          is waiting on them.
        */
        recordFilters={
          canReadAll
            ? [
                {
                  key: 'approvals-scope-all',
                  label: "Everyone's requests",
                  checked: scope === 'all',
                  permission: 'approvals.read-all',
                  onClick: () => setScope((s) => (s === 'all' ? 'mine' : 'all')),
                },
              ]
            : undefined
        }
        auditEntityTypes={['ApprovalRequest', 'ApprovalRequestStep']}
        rowKey={(request) => request.id}
        fetch={fetchRequests}
        reloadKey={reloadKey}
        searchPlaceholder="Search by reference, subject or submitter"
        emptyMessage={
          scope === 'all'
            ? 'No approval requests yet. One appears here when a module submits a record for sign-off.'
            : 'Nothing is waiting on you. A request appears here when you are named as an approver.'
        }
        // The anchor opens the request's own page. The READ key — opening is not deciding.
        onRowOpen={(request) => navigate(`/approvals/requests/${request.id}`)}
        openPermission="approvals.read"
        rowActions={rowActions}
        otherInfo={(request) => request}
      />

      {rejecting ? (
        <RejectApprovalModal
          open
          request={rejecting}
          stepCount={rejecting.totalSteps}
          onClose={() => setRejecting(null)}
          onRejected={() => {
            setRejecting(null);
            refresh();
          }}
        />
      ) : null}

    </>
  );
}

const COLUMNS: ColumnDef<ApprovalRequestListItem>[] = [
  {
    /*
      THE SUBJECT'S OWN CODE is the anchor, because that is what a reader calls the thing being
      approved — the workflow name titles every row of one kind alike, which is the mistake the
      family panel made with the relation.
    */
    key: 'subjectReference',
    header: 'Reference',
    width: 170,
    sortable: true,
    render: (request) => <TextCell value={request.subjectReference} mono />,
  },
  {
    key: 'workflowName',
    header: 'Workflow',
    width: 220,
    sortable: true,
    summary: true,
    render: (request) => <TextCell value={request.workflowName} />,
  },
  {
    key: 'subjectSummary',
    header: 'Subject',
    width: 320,
    flex: true,
    summary: true,
    render: (request) => <TextCell value={request.subjectSummary} />,
  },
  {
    key: 'requestedByName',
    header: 'Submitted by',
    width: 180,
    render: (request) => <TextCell value={request.requestedByName} />,
  },
  {
    /*
      SIGNED OF TOTAL on ONE line, under one header. Two columns would be two facts where the
      reader has one question — how far along is it — and neither number means anything alone.
    */
    key: 'progress',
    header: 'Signed',
    width: 100,
    align: 'right',
    render: (request) => (
      <TextCell value={`${request.signedSteps}/${request.totalSteps}`} mono numeric />
    ),
  },
  {
    /*
      THE DECISION IS PLAIN TEXT, and only live-or-deleted gets the pill — the disciplinary
      panel's split, and the budget plan's. TWO state columns deliberately: a deleted request
      and a rejected one are different facts, and a reader must not have to guess which the
      column is reporting.

      It is not a pill for a second reason, which is that there is no honest tone for it.
      `pending` is refused on an index table because amber is the brand colour, and `blocked`
      means a state that stops the record being used — which Pending, Cancelled and Retracted
      are not. Three of the five would have shared one tone and said nothing the label had not
      already said.
    */
    key: 'stage',
    /** The record's own lifecycle — the shell sits it beside the status pill. */
    stage: true,
    header: 'Decision',
    width: 130,
    sortable: true,
    render: (request) => <StatusText tone="inactive">{request.statusLabel}</StatusText>,
  },
  {
    key: 'completedAt',
    header: 'Decided',
    width: 170,
    sortable: true,
    render: (request) => <DateCell value={request.completedAt} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (request) =>
      request.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
